import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Field, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Col, Form, Row } from "react-bootstrap";
import CustomInput from "../../../CustomControls/CustomInput";
import { Spin } from "antd";
import CustomSelect from "../../../CustomControls/CustomSelect";
import { post } from "../../../helpers/api_helper";
import {
  POST_DDLGetReferenceStandardsByTestMasterID,
  POST_DDLGetTestMasterByCompanyID,
  POST_GetReferenceStandardTestMapDetailByID,
  POST_GetTestMasterStandardParameters,
  POST_ReferenceStandardTestInsert,
  POST_ReferenceStandardTestUpdate,
} from "../../../helpers/url_helper";
import {
  errorMessage,
  successMessage,
  warningMessage,
} from "../CommonFunctions";
import QuickAddReferenceStandard from "../ReferenceStandard/QuickAddReferenceStandard";

const AddReferenceStandardTestMapping = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { PrimaryIdParam, CopyReferenceIdParam } = useParams();

  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));
  const formsList = AuthUser?.Permissions?.FormList;

  const [testOptions, setTestOptions] = useState([]);
  const [referenceStandardOptions, setReferenceStandardOptions] = useState([]);
  const testParameterWithOptions = [
    {
      label: "Both",
      value: 1,
    },
    {
      label: "Min / Max",
      value: 2,
    },
    {
      label: "Requirement",
      value: 3,
    },
  ];

  const [
    showQuickAddReferenceStandardModal,
    setShowQuickAddReferenceStandardModal,
  ] = useState(false);

  const getTestDDL = async () => {
    setIsLoading(true);
    await post(POST_DDLGetTestMasterByCompanyID, {
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
      TestType: 1, // 0 - All, 1 - DT, 2 - NDT
    })
      .then((response) => {
        if (response?.IsSuccess) {
          setTestOptions(
            response?.List?.map((item) => ({
              label: item.TestName,
              value: item.TestMasterIDEncrypted,
            }))
          );

          /*select the value when there is only one option while insert only*/
          if (response?.List?.length == 1 && !PrimaryIdParam) {
            formik.setFieldValue(
              "TestMasterIDEncrypted",
              response?.List[0].TestMasterIDEncrypted
            );
            getTestStandardParameters(response?.List[0].TestMasterIDEncrypted);
          }
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const getReferenceStandardDDL = async (testMasterID) => {
    setIsLoading(true);
    await post(POST_DDLGetReferenceStandardsByTestMasterID, {
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
      TestMasterIDEncrypted: testMasterID,
    })
      .then((response) => {
        if (response?.IsSuccess) {
          formik.setFieldValue(
            "DepartmentIDEncrypted",
            response?.DepartmentIDEncrypted
          );
          formik.setFieldValue(
            "ProductGroupIDEncrypted",
            response?.ProductGroupIDEncrypted
          );

          setReferenceStandardOptions(
            response?.List?.map((item) => ({
              label: item.ReferenceStandard,
              value: item.ReferenceStandardIDEncrypted,
            }))
          );

          /*select the value when there is only one option while insert only*/
          if (response?.List?.length == 1 && !PrimaryIdParam) {
            formik.setFieldValue(
              "ReferenceStandardIDEncrypted",
              response?.List[0].ReferenceStandardIDEncrypted
            );
          } else {
            if (
              ReferenceStandardIDEncrypted &&
              response?.List?.filter(
                (x) =>
                  x.ReferenceStandardIDEncrypted == ReferenceStandardIDEncrypted
              )?.length == 0
            ) {
              formik.setFieldValue("ReferenceStandardIDEncrypted", "");
            }
          }
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const validationSchema = Yup.object().shape({
    TestMasterIDEncrypted: Yup.string().required("Test Name is required field"),
    ReferenceStandardIDEncrypted: Yup.string().required(
      "Reference Standard is required field"
    ),
  });

  const formik = useFormik({
    validationSchema: validationSchema,
    onSubmit: (values, formikHelpers) =>
      handleValidSubmit(values, formikHelpers),
    initialValues: {
      ReferenceStandardTestIDEncrypted: PrimaryIdParam
        ? window.atob(PrimaryIdParam)
        : "",
      TestMasterIDEncrypted: "",
      ReferenceStandardIDEncrypted: "",
      ListOfParameterCriteria: [],
      initialParametersList: {},
      DepartmentIDEncrypted: "",
      ProductGroupIDEncrypted: "",
      ParameterWith: 1,
    },
    validateOnBlur: false,
  });

  const {
    values: {
      ListOfParameterCriteria,
      initialParametersList,
      TestMasterIDEncrypted,
      ReferenceStandardIDEncrypted,
      DepartmentIDEncrypted,
      ProductGroupIDEncrypted,
      ParameterWith,
    },
  } = formik;

  useEffect(() => {
    if (TestMasterIDEncrypted) {
      getReferenceStandardDDL(TestMasterIDEncrypted);
    } else {
      setReferenceStandardOptions([]);
    }
  }, [TestMasterIDEncrypted]);

  const handleValidSubmit = (values) => {
    if (
      values?.ListOfParameterCriteria?.filter(
        (x) => x.SubGroup?.trim() == "" && x.IsDelete == false
      )?.length > 0 &&
      values?.ListOfParameterCriteria?.filter((x) => x.IsDelete == false)
        ?.length > 1
    ) {
      warningMessage("Please fill all the Group fields...");
      return;
    }

    let errorGroupStatus = false;
    values?.ListOfParameterCriteria?.filter(
      (x) => x.IsDelete == false
    )?.forEach((item, i) => {
      if (
        values?.ListOfParameterCriteria?.filter(
          (x) => x.IsDelete == false
        )?.filter(
          (x, ix) =>
            x.SubGroup?.toLowerCase()?.trim() ==
              item.SubGroup?.toLowerCase()?.trim() && i !== ix
        )?.length > 0
      ) {
        errorGroupStatus = true;
      }
    });
    if (errorGroupStatus == true) {
      warningMessage("Duplicate Group fields...");
      return;
    }

    let errorParaRangeValueStatus = false;
    let errorParaRangeValueDuplicateStatus = false;
    //let errorMinMaxStatus = false;
    values?.ListOfParameterCriteria?.filter(
      (x) => x.IsDelete == false
    )?.forEach((item, i) => {
      item?.ParametersList?.forEach((para, iP) => {
        if (para.IsParaProperty == true) {
          if (
            para?.RangeList?.filter(
              (x) => x.RangeValue?.trim() == "" && x.IsDelete == false
            )?.length > 0
          ) {
            errorParaRangeValueStatus = true;
          }

          para?.RangeList?.filter((x) => x.IsDelete == false)?.forEach(
            (range, iR) => {
              if (
                para?.RangeList?.filter((x) => x.IsDelete == false)?.filter(
                  (x, ix) =>
                    x.RangeValue?.toLowerCase()?.trim() ==
                      range.RangeValue?.toLowerCase()?.trim() && iR !== ix
                )?.length > 0
              ) {
                errorParaRangeValueDuplicateStatus = true;
              }
            }
          );
        }

        // if (
        //   para?.RangeList?.filter(
        //     (x) =>
        //       (x.MinValue?.trim() == "" || x.MaxValue?.trim() == "") &&
        //       x.IsDelete == false
        //   )?.length > 0
        // ) {
        //   errorMinMaxStatus = true;
        // }
      });
    });
    if (errorParaRangeValueStatus == true) {
      warningMessage("Please fill all the Parameter Range fields...");
      return;
    }
    if (errorParaRangeValueDuplicateStatus == true) {
      warningMessage("Duplicate Parameter Range fields...");
      return;
    }
    // if (errorMinMaxStatus == true) {
    //   warningMessage("Please fill all the Min - Max fields...");
    //   return;
    // }

    setIsLoading(true);
    let listOfPara = [];
    let listOfParaRange = [];
    Promise.all(
      values?.ListOfParameterCriteria?.map((item) => {
        item?.ParametersList?.map((p) => {
          if (
            p.ReferenceStandardParameterCriteriaIDEncrypted ||
            item.IsDelete == false
          ) {
            listOfPara = [
              ...listOfPara,
              {
                ReferenceStandardParameterCriteriaIDEncrypted:
                  p.ReferenceStandardParameterCriteriaIDEncrypted,
                ParameterIDEncrypted: p.ParameterIDEncrypted,
                IsParaProperty: p.IsParaProperty,
                SubGroup: item.SubGroup,
                IsDelete: item.IsDelete,
              },
            ];
            p?.RangeList?.map((r) => {
              if (
                r.ReferenceStandardParameterRangeIDEncrypted ||
                r.IsDelete == false
              ) {
                listOfParaRange = [
                  ...listOfParaRange,
                  {
                    ReferenceStandardParameterRangeIDEncrypted:
                      r.ReferenceStandardParameterRangeIDEncrypted,
                    ReferenceStandardParameterCriteriaIDEncrypted:
                      p.ReferenceStandardParameterCriteriaIDEncrypted,
                    ParameterIDEncrypted: p.ParameterIDEncrypted,
                    SubGroup: item.SubGroup,
                    RangeValue: r.RangeValue,
                    MinValue: r.MinValue,
                    MaxValue: r.MaxValue,
                    IsDelete: r.IsDelete,
                    Requirement: r.Requirement,
                    DefaultValue: r.DefaultValue,
                  },
                ];
              }
            });
          }
        });
      })
    );
    let payload = {
      ...values,
      ListOfRefStandardTestParameter: listOfPara,
      ListOfRefStandardTestParameterRange: listOfParaRange,
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
      CreatedByEncrypted: AuthUser?.ReferenceIDEncrypt
        ? AuthUser?.ReferenceIDEncrypt
        : "0",
      ModifiedByEncrypted: AuthUser?.ReferenceIDEncrypt
        ? AuthUser?.ReferenceIDEncrypt
        : "0",
    };
    delete payload["initialParametersList"];
    delete payload["ListOfParameterCriteria"];
    console.log("payload: ", payload);

    post(
      PrimaryIdParam
        ? POST_ReferenceStandardTestUpdate
        : POST_ReferenceStandardTestInsert,
      payload
    )
      .then((response) => {
        setIsLoading(true);
        if (response.IsSuccess) {
          successMessage(response?.Message);
          setTimeout(() => {
            navigate("/reference-standard-test-mapping");
          }, 1000);
        } else {
          errorMessage(response?.Message);
          setIsLoading(false);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getTestDDL();
    // getReferenceStandardDDL();
    if (PrimaryIdParam) {
      get_ReferenceStandardTestByID();
    }
    if (CopyReferenceIdParam) {
      get_ReferenceStandardTestByID();
    }
  }, []);

  const handleChangeTest = (e) => {
    formik.setFieldValue("TestMasterIDEncrypted", e?.value ?? "");
    if (e?.value) {
      getTestStandardParameters(e?.value);
    } else {
      formik.setFieldValue("initialParametersList", {});
      formik.setFieldValue("ListOfParameterCriteria", []);
    }
  };

  const getTestStandardParameters = (testID) => {
    setIsLoading(true);
    post(POST_GetTestMasterStandardParameters, {
      TestMasterIDEncrypted: testID,
    })
      .then((response) => {
        if (response?.IsSuccess) {
          let initList = {
            SubGroup: "",
            IsDelete: false,
            ParametersList: [],
          };
          response?.List?.map((item) => {
            initList.ParametersList = [
              ...initList.ParametersList,
              {
                ReferenceStandardParameterCriteriaIDEncrypted: "",
                ParameterIDEncrypted: item?.ParameterIDEncrypted,
                ParameterName: item?.ParameterName,
                IsParaProperty: false,
                IsNew: false,
                RangeList: [
                  {
                    ReferenceStandardParameterRangeIDEncrypted: "",
                    RangeValue: "",
                    MinValue: "",
                    MaxValue: "",
                    IsDelete: false,
                    Requirement: "",
                    DefaultValue: "",
                  },
                ],
              },
            ];
          });

          formik.setFieldValue("initialParametersList", initList);
          formik.setFieldValue("ListOfParameterCriteria", [initList]);
        } else {
          errorMessage(response?.Message);
          formik.setFieldValue("initialParametersList", {});
          formik.setFieldValue("ListOfParameterCriteria", []);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const get_ReferenceStandardTestByID = async () => {
    setIsLoading(true);
    await post(POST_GetReferenceStandardTestMapDetailByID, {
      ReferenceStandardTestIDEncrypted: CopyReferenceIdParam
        ? window.atob(CopyReferenceIdParam)
        : window.atob(PrimaryIdParam),
    })
      .then((response) => {
        if (response?.IsSuccess) {
          formik.setFieldValue(
            "TestMasterIDEncrypted",
            response?.TestMasterIDEncrypted
          );
          formik.setFieldValue(
            "ReferenceStandardIDEncrypted",
            CopyReferenceIdParam ? "" : response?.ReferenceStandardIDEncrypted
          );
          formik.setFieldValue(
            "ListOfParameterCriteria",
            response?.SubGroupList
          );
          formik.setFieldValue(
            "ParameterWith",
            response?.ParameterWith > 0 ? response?.ParameterWith : 1
          );

          let initList = {
            SubGroup: "",
            IsDelete: false,
            ParametersList: [],
          };

          response?.SubGroupList[0]?.ParametersList?.map((item) => {
            initList.ParametersList = [
              ...initList.ParametersList,
              {
                ReferenceStandardParameterCriteriaIDEncrypted: "",
                ParameterIDEncrypted: item?.ParameterIDEncrypted,
                ParameterName: item?.ParameterName,
                IsParaProperty: item?.IsParaProperty,
                IsNew: false,
                RangeList: item?.RangeList?.map((x) => {
                  return {
                    ReferenceStandardParameterRangeIDEncrypted: "",
                    RangeValue: x.RangeValue,
                    MinValue: "",
                    MaxValue: "",
                    IsDelete: false,
                    Requirement: "",
                    DefaultValue: "",
                  };
                }),
              },
            ];
          });
          formik.setFieldValue("initialParametersList", initList);
        } else {
          errorMessage(response?.Message);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const handleChangeRangeValue = (value, iPara, iRange) => {
    formik.setFieldValue(
      "ListOfParameterCriteria",
      ListOfParameterCriteria?.map((item, idx) => {
        return {
          ...item,
          ParametersList: item?.ParametersList?.map((e, idxP) => {
            if (iPara == idxP) {
              return {
                ...e,
                RangeList: e.RangeList?.map((r, idxR) => {
                  if (iRange == idxR) {
                    return {
                      ...r,
                      RangeValue: value,
                    };
                  } else {
                    return { ...r };
                  }
                }),
              };
            } else {
              return { ...e };
            }
          }),
        };
      })
    );
  };

  const addNewGroup = () => {
    formik.setFieldValue("ListOfParameterCriteria", [
      ...ListOfParameterCriteria,
      {
        ...initialParametersList,
        ParametersList: initialParametersList?.ParametersList?.map(
          (e, idxP) => {
            return {
              ...e,
              RangeList: e.RangeList?.map((r, idxR) => {
                return {
                  ...r,
                  RangeValue:
                    ListOfParameterCriteria[0].ParametersList[idxP].RangeList[
                      idxR
                    ].RangeValue,
                };
              }),
            };
          }
        ),
      },
    ]);
  };

  const removeGroup = (i) => {
    if (
      ListOfParameterCriteria?.filter((x) => x.IsDelete == false)?.length < 2
    ) {
      warningMessage("Atleast one Group is required");
    } else {
      let newList = ListOfParameterCriteria?.map((item, idx) => {
        if (i == idx) {
          return {
            ...item,
            IsDelete: true,
            ParametersList: item?.ParametersList?.map((x) => {
              return {
                ...x,
                RangeList: x.RangeList?.map((r) => {
                  return { ...r, IsDelete: true };
                }),
              };
            }),
          };
        } else {
          return { ...item };
        }
      });
      formik.setFieldValue(
        "ListOfParameterCriteria",
        //ListOfParameterCriteria?.filter((x, idx) => idx != i)
        newList
      );
    }
  };

  const addNewRange = (i) => {
    let newInit = {
      ...initialParametersList,
      ParametersList: initialParametersList?.ParametersList?.map((e, idxP) => {
        if (i == idxP) {
          return {
            ...e,
            IsParaProperty: true,
            RangeList: [
              ...e.RangeList,
              {
                ReferenceStandardParameterRangeIDEncrypted: "",
                RangeValue: "",
                MinValue: "",
                MaxValue: "",
                IsDelete: false,
                Requirement: "",
                DefaultValue: "",
              },
            ],
          };
        } else {
          return { ...e };
        }
      }),
    };
    formik.setFieldValue("initialParametersList", newInit);

    let newList = ListOfParameterCriteria?.map((item, idx) => {
      return {
        ...item,
        ParametersList: item?.ParametersList?.map((e, idxP) => {
          if (i == idxP) {
            return {
              ...e,
              IsParaProperty: true,
              RangeList: [
                ...e.RangeList,
                {
                  ReferenceStandardParameterRangeIDEncrypted: "",
                  RangeValue: "",
                  MinValue: "",
                  MaxValue: "",
                  IsDelete: false,
                  Requirement: "",
                  DefaultValue: "",
                },
              ],
            };
          } else {
            return { ...e };
          }
        }),
      };
    });
    formik.setFieldValue("ListOfParameterCriteria", newList);
  };

  const removeRange = (i, iR) => {
    let IsRangeLength = true;
    ListOfParameterCriteria?.filter((x) => x.IsDelete == false)?.map(
      (item, idx) => {
        if (
          item?.ParametersList[i].RangeList.filter((x) => x.IsDelete == false)
            ?.length > 1
        ) {
          IsRangeLength = true;
        } else {
          IsRangeLength = false;
          return;
        }
      }
    );

    if (IsRangeLength == false) {
      warningMessage("Atleast one Range is required");
    } else {
      let newInit = {
        ...initialParametersList,
        ParametersList: initialParametersList?.ParametersList?.map(
          (e, idxP) => {
            if (i == idxP) {
              return {
                ...e,
                IsParaProperty:
                  e.RangeList?.filter((x) => x.IsDelete == false)?.length > 2
                    ? true
                    : false,
                //RangeList: e.RangeList?.filter((r, idxR) => idxR != iR),
                RangeList: e.RangeList?.map((r, idxR) => {
                  if (iR == idxR) {
                    return { ...r, IsDelete: true };
                  } else {
                    if (
                      e.RangeList?.filter((x) => x.IsDelete == false)?.length >
                      2
                    ) {
                      return { ...r };
                    } else {
                      return { ...r, RangeValue: "" };
                    }
                  }
                }),
              };
            } else {
              return { ...e };
            }
          }
        ),
      };
      formik.setFieldValue("initialParametersList", newInit);

      let newList = ListOfParameterCriteria?.map((item, idx) => {
        return {
          ...item,
          ParametersList: item?.ParametersList?.map((e, idxP) => {
            if (i == idxP) {
              return {
                ...e,
                IsParaProperty:
                  e.RangeList?.filter((x) => x.IsDelete == false)?.length > 2
                    ? true
                    : false,
                //RangeList: e.RangeList?.filter((r, idxR) => idxR != iR),
                RangeList: e.RangeList?.map((r, idxR) => {
                  if (iR == idxR) {
                    return { ...r, IsDelete: true };
                  } else {
                    if (
                      e.RangeList?.filter((x) => x.IsDelete == false)?.length >
                      2
                    ) {
                      return { ...r };
                    } else {
                      return { ...r, RangeValue: "" };
                    }
                  }
                }),
              };
            } else {
              return { ...e };
            }
          }),
        };
      });
      formik.setFieldValue("ListOfParameterCriteria", newList);
    }
  };

  const addReferenceStandard = () => {
    if (TestMasterIDEncrypted) {
      setShowQuickAddReferenceStandardModal(true);
    } else {
      warningMessage("Please select Test Name first...");
    }
  };

  const fnCallbackQuickAddReferenceStandard = (
    referenceStandardID,
    referenceStandardName
  ) => {
    setReferenceStandardOptions([
      ...referenceStandardOptions,
      { label: referenceStandardName, value: referenceStandardID },
    ]);
    formik.setFieldValue("ReferenceStandardIDEncrypted", referenceStandardID);
  };

  return (
    <div className="card m-1">
      <div className="card-body p-1">
        <Spin
          size="large"
          spinning={isLoading}
          tip={"Loading..."}
          style={{ position: "fixed" }}
        >
          <div className="tbl-caption p-2">
            <h4 className="heading mb-0">
              {PrimaryIdParam ? "Edit" : "Add"} Reference Standard Test Mapping
            </h4>
          </div>
          <hr className="mt-0 mb-1"></hr>
          <div className="p-2 pt-0">
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                <Row>
                  <Col lg={4}>
                    <Field
                      label="Test Name"
                      name="TestMasterIDEncrypted"
                      placeholder={"Select Test Name"}
                      component={CustomSelect}
                      options={testOptions}
                      required
                      isDisabled={PrimaryIdParam ? true : false}
                      onChange={handleChangeTest}
                      autoFocus
                    />
                  </Col>
                  <Col lg={4}>
                    {AuthUser?.UserType !== 1 ||
                    formsList?.filter((x) => x?.FormID == 23 && x?.IsAdd)
                      ?.length > 0 ? (
                      <Link
                        to="#"
                        className="icon-box icon-box-md bg-primary-light me-1"
                        title="New Reference Standard"
                        style={{ float: "right" }}
                        onClick={() => {
                          addReferenceStandard();
                        }}
                      >
                        <i className="fa fa-plus"></i>
                      </Link>
                    ) : (
                      ""
                    )}
                    <Field
                      label="Reference Standard"
                      name="ReferenceStandardIDEncrypted"
                      placeholder={"Select Reference Standard"}
                      component={CustomSelect}
                      options={referenceStandardOptions}
                      required
                    />
                  </Col>
                  <Col lg={2}>
                    <Field
                      label="Parameter With"
                      name="ParameterWith"
                      placeholder={"Select Parameter With"}
                      component={CustomSelect}
                      options={testParameterWithOptions}
                      isClearable={false}
                    />
                  </Col>
                </Row>
                <hr className="mt-1 mb-2"></hr>
                <Row>
                  <Col lg={12}>
                    <div
                      id="ParameterCriteria-tbl_wrapper"
                      className="dataTables_wrapper table-responsive no-footer"
                    >
                      <table
                        id="ParameterCriteria-tbl"
                        className="table table-bordered no-footer mb-2"
                      >
                        <thead>
                          <tr key={0}>
                            <th rowSpan={3} style={{ minWidth: "30px" }}></th>
                            <th
                              rowSpan={3}
                              style={{
                                minWidth: "150px",
                              }}
                              className="p-1"
                            >
                              <div
                                style={{
                                  textAlign: "right",
                                  width: "100%",
                                }}
                                className="mb-2 pe-1"
                              >
                                Range
                              </div>
                              <div
                                style={{
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                  width: "100%",
                                }}
                              >
                                Group / Grade
                              </div>
                            </th>
                            {ListOfParameterCriteria?.length > 0 &&
                              ListOfParameterCriteria?.filter(
                                (x) => x.IsDelete == false
                              )[0]?.ParametersList?.map((item, i) => (
                                <th
                                  key={i}
                                  colSpan={
                                    item?.RangeList?.filter(
                                      (x) => x.IsDelete == false
                                    )?.length *
                                    ((ParameterWith == 1 || ParameterWith == 2
                                      ? 2
                                      : 1) +
                                      (ParameterWith == 1 || ParameterWith == 2
                                        ? 1
                                        : 0) +
                                      (ParameterWith == 1 || ParameterWith == 3
                                        ? 1
                                        : 0))
                                  }
                                  style={{
                                    textAlign: "center",
                                    padding: 2,
                                  }}
                                  className={
                                    item?.IsNew == true ? "newPara" : ""
                                  }
                                >
                                  {item.ParameterName}
                                </th>
                              ))}
                          </tr>
                          <tr key={1}>
                            {ListOfParameterCriteria?.length > 0 &&
                              ListOfParameterCriteria?.filter(
                                (x) => x.IsDelete == false
                              )[0]?.ParametersList?.map((item, i) =>
                                item?.RangeList?.map((x, ix) => (
                                  <th
                                    key={ix}
                                    colSpan={
                                      ParameterWith == 1
                                        ? 4
                                        : ParameterWith == 2
                                        ? 3
                                        : 2
                                    }
                                    style={{
                                      padding: 1,
                                      display:
                                        x?.IsDelete == true ? "none" : "",
                                    }}
                                    className={
                                      item?.IsNew == true ? "newPara" : ""
                                    }
                                  >
                                    <div className="d-flex">
                                      {item?.IsParaProperty == true ? (
                                        <div style={{ width: "100%" }}>
                                          <Field
                                            name={`ListOfParameterCriteria[0].ParametersList[${i}].RangeList[${ix}].RangeValue`}
                                            component={CustomInput}
                                            noMarginBottom={true}
                                            maxLength={100}
                                            onChange={(e) => {
                                              formik.setFieldValue(
                                                `ListOfParameterCriteria[0].ParametersList[${i}].RangeList[${ix}].RangeValue`,
                                                e?.target?.value ?? ""
                                              );
                                              handleChangeRangeValue(
                                                e?.target?.value ?? "",
                                                i,
                                                ix
                                              );
                                            }}
                                          />
                                        </div>
                                      ) : (
                                        <div style={{ width: "100%" }}></div>
                                      )}
                                      <div className="m-1 mt-2">
                                        <Link
                                          to="#"
                                          className="icon-box icon-box-md bg-primary-light me-1"
                                          title="New Range"
                                          onClick={(e) => {
                                            addNewRange(i);
                                            e.preventDefault();
                                          }}
                                        >
                                          <i className="fa fa-plus"></i>
                                        </Link>
                                        {item?.RangeList?.filter(
                                          (x) => x.IsDelete == false
                                        )?.length > 1 ? (
                                          <Link
                                            to="#"
                                            className="icon-box icon-box-md bg-danger-light"
                                            title="Remove Range"
                                            onClick={(e) => {
                                              removeRange(i, ix);
                                              e.preventDefault();
                                            }}
                                          >
                                            <i className="fa fa-minus"></i>
                                          </Link>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      {/* {ix == item?.RangeList?.length - 1 ? (
                                        <div className="m-1 mt-2">
                                          <Link
                                            to="#"
                                            className="icon-box icon-box-md bg-primary-light"
                                            title="New Range"
                                            onClick={(e) => {
                                              addNewRange(i);
                                              e.preventDefault();
                                            }}
                                          >
                                            <i className="fa fa-plus"></i>
                                          </Link>
                                         
                                        </div>
                                      ) : (
                                        <div className="mt-2 ms-1 me-1">
                                          <Link
                                            to="#"
                                            className="icon-box icon-box-md bg-danger-light"
                                            title="Remove Range"
                                            onClick={(e) => {
                                              removeRange(i, ix);
                                              e.preventDefault();
                                            }}
                                          >
                                            <i className="fa fa-minus"></i>
                                          </Link>
                                        </div>
                                      )} */}
                                    </div>
                                  </th>
                                ))
                              )}
                          </tr>
                          <tr key={2}>
                            {ListOfParameterCriteria?.length > 0 &&
                              ListOfParameterCriteria?.filter(
                                (x) => x.IsDelete == false
                              )[0]?.ParametersList?.map((item, i) =>
                                item?.RangeList?.map((r, ir) => (
                                  <React.Fragment key={ir}>
                                    {(ParameterWith == 1 ||
                                      ParameterWith == 2) && (
                                      <React.Fragment key={ir}>
                                        <th
                                          style={{
                                            textAlign: "center",
                                            padding: 1,
                                            minWidth: "90px",
                                            display:
                                              r?.IsDelete == true ? "none" : "",
                                          }}
                                          className={
                                            item?.IsNew == true ? "newPara" : ""
                                          }
                                        >
                                          Min
                                        </th>
                                        <th
                                          style={{
                                            textAlign: "center",
                                            padding: 1,
                                            minWidth: "90px",
                                            display:
                                              r?.IsDelete == true ? "none" : "",
                                          }}
                                          className={
                                            item?.IsNew == true ? "newPara" : ""
                                          }
                                        >
                                          Max
                                        </th>
                                      </React.Fragment>
                                    )}
                                    {(ParameterWith == 1 ||
                                      ParameterWith == 3) && (
                                      <th
                                        style={{
                                          textAlign: "center",
                                          padding: 1,
                                          minWidth: "200px",
                                          display:
                                            r?.IsDelete == true ? "none" : "",
                                        }}
                                        className={
                                          item?.IsNew == true ? "newPara" : ""
                                        }
                                      >
                                        Requirement
                                      </th>
                                    )}
                                    <th
                                      style={{
                                        textAlign: "center",
                                        padding: 1,
                                        minWidth: "120px",
                                        display:
                                          r?.IsDelete == true ? "none" : "",
                                      }}
                                      className={
                                        item?.IsNew == true ? "newPara" : ""
                                      }
                                    >
                                      Default Value
                                    </th>
                                  </React.Fragment>
                                ))
                              )}
                          </tr>
                        </thead>
                        <tbody>
                          {ListOfParameterCriteria?.length > 0
                            ? ListOfParameterCriteria?.map((item, i) => (
                                <tr
                                  key={i}
                                  style={{
                                    display:
                                      item?.IsDelete == true ? "none" : "",
                                  }}
                                >
                                  <td>
                                    <Link
                                      to="#"
                                      className="icon-box icon-box-md bg-primary-light"
                                      title="New Group"
                                      onClick={(e) => {
                                        addNewGroup();
                                        e.preventDefault();
                                      }}
                                    >
                                      <i className="fa fa-plus"></i>
                                    </Link>
                                    {ListOfParameterCriteria?.filter(
                                      (x) => x.IsDelete == false
                                    )?.length > 1 ? (
                                      <Link
                                        to="#"
                                        className="icon-box icon-box-md bg-danger-light ms-1"
                                        title="Remove Group"
                                        onClick={(e) => {
                                          removeGroup(i);
                                          e.preventDefault();
                                        }}
                                      >
                                        <i className="fa fa-minus"></i>
                                      </Link>
                                    ) : (
                                      ""
                                    )}
                                  </td>
                                  <td>
                                    <Field
                                      name={`ListOfParameterCriteria[${i}].SubGroup`}
                                      component={CustomInput}
                                      noMarginBottom={true}
                                      maxLength={100}
                                    />
                                  </td>
                                  {item?.ParametersList?.map((para, ip) =>
                                    para?.RangeList?.map((r, ir) => (
                                      <React.Fragment key={ir}>
                                        {(ParameterWith == 1 ||
                                          ParameterWith == 2) && (
                                          <React.Fragment key={ir}>
                                            <td
                                              style={{
                                                display:
                                                  r?.IsDelete == true
                                                    ? "none"
                                                    : "",
                                              }}
                                              className={
                                                para?.IsNew == true
                                                  ? "newPara"
                                                  : ""
                                              }
                                            >
                                              <Field
                                                name={`ListOfParameterCriteria[${i}].ParametersList[${ip}].RangeList[${ir}].MinValue`}
                                                component={CustomInput}
                                                noMarginBottom={true}
                                                maxLength={100}
                                              />
                                            </td>
                                            <td
                                              style={{
                                                display:
                                                  r?.IsDelete == true
                                                    ? "none"
                                                    : "",
                                              }}
                                              className={
                                                para?.IsNew == true
                                                  ? "newPara"
                                                  : ""
                                              }
                                            >
                                              <Field
                                                name={`ListOfParameterCriteria[${i}].ParametersList[${ip}].RangeList[${ir}].MaxValue`}
                                                component={CustomInput}
                                                noMarginBottom={true}
                                                maxLength={100}
                                              />
                                            </td>
                                          </React.Fragment>
                                        )}
                                        {(ParameterWith == 1 ||
                                          ParameterWith == 3) && (
                                          <td
                                            style={{
                                              display:
                                                r?.IsDelete == true
                                                  ? "none"
                                                  : "",
                                            }}
                                            className={
                                              para?.IsNew == true
                                                ? "newPara"
                                                : ""
                                            }
                                          >
                                            <Field
                                              name={`ListOfParameterCriteria[${i}].ParametersList[${ip}].RangeList[${ir}].Requirement`}
                                              component={CustomInput}
                                              noMarginBottom={true}
                                              maxLength={500}
                                            />
                                          </td>
                                        )}
                                        <td
                                          style={{
                                            display:
                                              r?.IsDelete == true ? "none" : "",
                                          }}
                                          className={
                                            para?.IsNew == true ? "newPara" : ""
                                          }
                                        >
                                          <Field
                                            name={`ListOfParameterCriteria[${i}].ParametersList[${ip}].RangeList[${ir}].DefaultValue`}
                                            component={CustomInput}
                                            noMarginBottom={true}
                                            maxLength={500}
                                          />
                                        </td>
                                      </React.Fragment>
                                    ))
                                  )}
                                </tr>
                              ))
                            : ""}
                        </tbody>
                      </table>
                    </div>
                  </Col>
                </Row>
                <hr className="mt-1 mb-2"></hr>
                <button
                  type="submit"
                  //  className="btn btn-primary"
                  className="btn btn-primary btn-sm me-2"
                >
                  <i className="fa-solid fa-floppy-disk"></i> Submit
                </button>
                <button
                  type="button"
                  // className="btn btn-danger light"
                  className="btn btn-danger btn-sm"
                  onClick={(e) => {
                    navigate(-1);
                  }}
                >
                  <i className="fa fa-cancel"></i> Cancel
                </button>
              </Form>
            </FormikProvider>
          </div>
        </Spin>
        <QuickAddReferenceStandard
          departmentID={DepartmentIDEncrypted}
          productGroupID={ProductGroupIDEncrypted}
          showModal={showQuickAddReferenceStandardModal}
          setShowModal={setShowQuickAddReferenceStandardModal}
          fnCallback={fnCallbackQuickAddReferenceStandard}
        />
      </div>
    </div>
  );
};

export default AddReferenceStandardTestMapping;
